import { Alert, Skeleton } from "@hakistudio/hakiui";
import { CalendarDays } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import {
  CONNECT_RESULTS,
  useConnectGoogle,
  useDisconnectGoogle,
  useGoogleStatus,
} from "@/lib/google";

/**
 * Connect / disconnect the user's Google Calendar.
 *
 * This is the only route in for someone who signed up with an email and
 * password; people who came in through Google get the same consent offered
 * automatically on first landing, and end up back here afterwards.
 */
export function GoogleCalendarCard() {
  const { data: status, isPending, error: statusError } = useGoogleStatus();
  const connect = useConnectGoogle();
  const disconnect = useDisconnectGoogle();
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  /**
   * Reads the outcome Google's callback appended before dropping it from the URL
   * — otherwise a reload would resurrect a stale "connected" banner. The param
   * can arrive before or after the hash depending on how the shell came back.
   */
  useEffect(() => {
    const fromSearch = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.split("?")[1] ?? "";
    const outcome = fromSearch.get("google") ?? new URLSearchParams(hashQuery).get("google");
    if (!outcome) return;

    setResult(CONNECT_RESULTS[outcome] ?? null);
    fromSearch.delete("google");
    const query = fromSearch.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${
        window.location.hash.split("?")[0] ?? ""
      }`,
    );
  }, []);

  const error = connect.error ?? disconnect.error;

  if (isPending) return <Skeleton height="6rem" radius="lg" />;

  // A failed request is not the same claim as "the server has no Google client",
  // and saying the latter sends people off configuring something that is fine.
  if (statusError) {
    return (
      <Alert variant="danger" title="Could not reach the Sebas API">
        {(statusError as Error).message}
      </Alert>
    );
  }

  if (!status?.configured) {
    return (
      <Alert variant="info" title="Google Calendar is not set up on this server">
        Add GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET and GOOGLE_TOKEN_KEY to the Worker to enable it.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-card border border-line bg-raised p-5">
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-sunken text-ink-muted">
          <CalendarDays className="h-5 w-5" />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="text-base font-semibold">Google Calendar</span>
          <span className="text-sm text-ink-muted">
            {status.connected
              ? `Showing events from ${status.googleEmail ?? "your Google account"}.`
              : "Show your Google events alongside your Sebas calendar. Read-only — Sebas never writes to it."}
          </span>
        </div>
      </div>

      {result ? (
        <Alert variant={result.ok ? "success" : "warning"} title={result.message} />
      ) : null}
      {error ? <Alert variant="danger" title={String((error as Error).message)} /> : null}

      <div className="flex flex-wrap gap-2">
        {status.connected ? (
          <Button
            variant="secondary"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
          >
            {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </Button>
        ) : (
          <Button onClick={() => connect.mutate()} disabled={connect.isPending}>
            {connect.isPending ? "Opening Google…" : "Connect Google Calendar"}
          </Button>
        )}
      </div>
    </div>
  );
}
